import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as CrownIcon } from '@repo/common/assets/icons/16px/icon_16px_crown.svg';
import { ReactComponent as HeartFilledIcon } from '@repo/common/assets/icons/32px/icon_32px_heart_filled.svg';
import { ReactComponent as HeartOutlineIcon } from '@repo/common/assets/icons/32px/icon_32px_heart_outline.svg';
import { ReactComponent as ShoppingBagIcon } from '@repo/common/assets/icons/32px/icon_32px_shopping-bag_outline_add.svg';
import {
  applyTypography,
  BaseButton,
  Chip,
  useAuth,
  LoginRequiredModal,
  trackCtaClick,
  trackWorkshopBookStart,
  useT,
} from '@repo/common';
import DateSelectionModal from '../DateSelectionModal';
import KeywordPillList from './KeywordPillList';
import QuickAddBlockedModal from '../Modal/QuickAddBlockedModal';
import { getWorkshopDetail } from '../../../api/workshop/workshop.api';
import { transformDateSlots } from '../utils/dateSlotUtils';

const TAG_TONE_CLASSES = {
  accent: {
    bg: 'bg-orange-orange02',
    ring: 'ring-orange-orange03',
    text: 'text-orange-orange04',
  },
  neutral: {
    bg: 'bg-grayscale-gray02',
    ring: 'ring-grayscale-gray00',
    text: 'text-grayscale-gray07',
  },
};

const Tag = ({ label, tone = 'neutral' }) => {
  const isHit = label?.toLowerCase() === 'hit';
  const toneClasses =
    tone === 'accent' ? TAG_TONE_CLASSES.accent : TAG_TONE_CLASSES.neutral;

  // Chip 의 soft-50 마스크가 박스 밖 그림자를 잘라내므로 그림자는 바깥 래퍼에 준다
  return (
    <span className='inline-flex rounded-full shadow-2'>
      <Chip
        type='fill'
        state='disabled'
        icon={
          <CrownIcon className='[&_path]:fill-current' aria-hidden='true' />
        }
        hideIcon={!isHit}
        disableTypography
        disableDefaultColors
        bgclass={`h-8 min-w-[60px] gap-1 px-[10px] py-2 ${toneClasses.bg}`}
        borderclass={toneClasses.ring}
        textclass={`typo-b12Strong ${toneClasses.text}`}
      >
        {label}
      </Chip>
    </span>
  );
};

// 카드 너비를 넘는 칩은 KeywordPillList 가 개수를 줄이고 "…" 칩을 붙인다
// title 툴팁(숨은 키워드 목록)이 떠야 해서 disabled(pointer-events-none) 대신 hover 효과만 끈다
const KeywordPill = (label, key, title) => (
  <Chip
    key={key}
    title={title || label}
    type='fill'
    hideIcon
    tabIndex={-1}
    disableTypography
    disableDefaultColors
    bgclass='h-[26px] min-w-0 max-w-full shrink-0 overflow-hidden rounded-lg bg-grayscale-gray02 px-[10px] py-1 tabletH:h-7 tabletH:px-3 tabletH:py-[6px] [&>span]:min-w-0 [&>span]:truncate'
    borderclass='ring-0'
    textclass='typo-b12Strong text-grayscale-gray05 tabletH:typo-b13Strong'
    hoverclass='hover:opacity-100'
  >
    {label}
  </Chip>
);

const WorkshopCard = ({
  id,
  title,
  description,
  keywords = [],
  tags = [],
  image,
  featured = false,
  ctaLabel,
  isWishlisted = false,
  onToggleWishlist,
  onDateSelected,
  stepNumber,
  stepName,
}) => {
  const t = useT();
  const navigate = useNavigate();
  const HeartIcon = isWishlisted ? HeartFilledIcon : HeartOutlineIcon;
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [dateSlots, setDateSlots] = useState([]);
  const [isBlocked, setIsBlocked] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [isQuickAddLoading, setIsQuickAddLoading] = useState(false);
  const { isLoggedIn } = useAuth();

  const trackBookingStart = () => {
    trackWorkshopBookStart({
      workshop_id: id,
      workshop_name: title,
      step_number: stepNumber,
      step_name: stepName,
      source: 'workshop_feed',
    });
  };

  const handleCardClick = () => {
    // 카드 자식으로 렌더되는 모달(날짜 선택/로그인/차단)이 열려 있으면
    // 모달 내부 탭이 카드 네비게이션으로 새지 않도록 이동을 막는다.
    // (포털 이벤트가 React 트리상 부모인 article로 버블되어 모바일에서 stopPropagation만으로는 막히지 않음)
    if (isDateModalOpen || showLoginModal || showBlockedModal) return;
    if (id) {
      navigate(`/community/kpathport/workshop/${id}`);
    }
  };

  const handleWishlistToggle = (event) => {
    event.stopPropagation();
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }
    onToggleWishlist?.(!isWishlisted);
  };

  const openQuickAddModal = async () => {
    if (!id || isQuickAddLoading) return;

    setIsQuickAddLoading(true);
    try {
      const data = await getWorkshopDetail(parseInt(id, 10));
      const blocked = Boolean(data?.isBlocked);
      setIsBlocked(blocked);

      if (blocked) {
        setShowBlockedModal(true);
        return;
      }

      setDateSlots(transformDateSlots(data?.dateSlots));
      setIsDateModalOpen(true);
    } catch (e) {
      console.error('Quick add dateSlots fetch failed:', e);
      setDateSlots([]);
      setIsDateModalOpen(true);
    } finally {
      setIsQuickAddLoading(false);
    }
  };

  const handleQuickAddClick = (event) => {
    event.stopPropagation();
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }
    trackBookingStart();
    openQuickAddModal();
  };

  const handleMobileBagClick = (event) => {
    event.stopPropagation();
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }
    trackBookingStart();
    openQuickAddModal();
  };

  const handleCloseDateModal = () => {
    setIsDateModalOpen(false);
  };

  return (
    <article
      className='flex flex-col w-full pb-10 cursor-pointer group tabletH:gap-4 tabletH:pb-0'
      onClick={handleCardClick}
    >
      <div className='relative flex h-[240px] w-full flex-col items-start justify-between rounded-[20px] bg-grayscale-gray01 px-4 py-4 tabletH:h-auto tabletH:aspect-[362/280] tabletH:rounded-[30px] tabletH:px-6 tabletH:py-5'>
        {/* Background Image */}
        <div className='absolute inset-0 pointer-events-none rounded-[20px] overflow-hidden tabletH:rounded-[30px]'>
          <div className='absolute inset-0 rounded-[20px] bg-grayscale-gray01 tabletH:rounded-[30px]' />
          <img
            src={image}
            alt={title}
            loading='lazy'
            decoding='async'
            className='absolute inset-0 w-full h-full object-cover object-center rounded-[20px] tabletH:rounded-[30px]'
          />
          {/* Gradient Overlay - Visible on hover (desktop only) */}
          <div className='absolute inset-0 hidden rounded-[30px] bg-gradient-to-b from-transparent via-transparent to-[rgba(0,0,0,0.5)] opacity-0 transition-opacity duration-300 ease-out group-hover:opacity-100 tabletH:block' />
        </div>

        {/* Top Section: Tags and Heart Icon */}
        <div className='relative z-10 flex items-center justify-between w-full gap-2'>
          <div className='flex flex-row items-center flex-1'>
            {tags?.length > 0 && (
              <div className='flex items-start flex-1 gap-2'>
                {tags.map((tag) => (
                  <Tag
                    key={`${title}-${tag.label}`}
                    label={tag.label}
                    tone={tag.tone}
                  />
                ))}
              </div>
            )}
          </div>
          <div className='relative w-8 h-8 shrink-0'>
            <BaseButton
              type='text'
              size='s'
              onClick={handleWishlistToggle}
              aria-label={
                isWishlisted
                  ? t('remove from wishlist', '위시리스트에서 삭제')
                  : t('add to wishlist', '위시리스트에 추가')
              }
              aria-pressed={isWishlisted}
              className={`h-full w-full min-w-0 border-0 bg-transparent p-0 hover:bg-transparent [&>span]:h-full ${
                isWishlisted
                  ? 'text-icon-failed hover:text-icon-failed'
                  : 'text-icon-inverse hover:text-icon-inverse'
              }`}
              icon={
                <HeartIcon className='w-full h-full overflow-visible [&_path]:fill-current [&_path]:stroke-current' />
              }
            />
          </div>
        </div>

        {/* Bottom Section: Quick Add Button */}
        {ctaLabel && (
          <>
            <div className='relative z-10 hidden w-full transition-opacity duration-300 ease-out opacity-0 tabletH:block group-hover:opacity-100'>
              <BaseButton
                type='primary'
                size='l'
                hideIcon
                width='100%'
                hoverclass='hover:bg-green-deep02 hover:border-green-deep02'
                activeclass='active:bg-green-deep02 active:border-green-deep02'
                onClick={handleQuickAddClick}
                isDisabled={isQuickAddLoading}
              >
                {ctaLabel}
              </BaseButton>
            </div>

            <BaseButton
              type='primary'
              size='l'
              onClick={handleMobileBagClick}
              aria-label={t('Open date selection', '날짜 선택 열기')}
              className='absolute z-10 w-12 h-12 min-w-0 rounded-full border-0 p-0 tabletH:hidden bottom-4 right-4 bg-green-deep02 shadow-2'
              icon={
                <ShoppingBagIcon className='w-8 h-8 text-icon-inverse [&_path]:stroke-current' />
              }
            />
          </>
        )}
      </div>

      {/* Text Content Section */}
      <div className='flex w-full flex-col items-start px-0 pt-2 tabletH:px-[15px] tabletH:pt-0'>
        <div className='flex items-center justify-center w-full pb-1 tabletH:pb-0'>
          {/* Desktop Title */}
          <p
            className={`hidden tabletH:block flex-1 ${applyTypography(
              'title2',
            )} truncate text-text-primary`}
          >
            {title}
          </p>
          {/* Mobile Title */}
          <p
            className={`flex-1 tabletH:hidden ${applyTypography(
              'title3',
            )} truncate text-text-primary`}
          >
            {title}
          </p>
        </div>
        <div className='flex w-full items-center justify-center pb-2 tabletH:pb-0 tabletH:pt-[6px]'>
          {/* Desktop Description */}
          <p
            className={`hidden tabletH:block flex-1 ${applyTypography(
              'b13',
            )} truncate text-text-placeholder`}
          >
            {description}
          </p>
          {/* Mobile Description */}
          <p
            className={`flex-1 tabletH:hidden ${applyTypography(
              'b12',
            )} truncate text-text-placeholder`}
          >
            {description}
          </p>
        </div>
        <KeywordPillList
          items={keywords}
          renderPill={KeywordPill}
          className='flex h-[26px] w-full min-w-0 items-start gap-2 overflow-hidden tabletH:mt-4 tabletH:h-7'
        />
      </div>

      <DateSelectionModal
        isOpen={isDateModalOpen}
        onClose={handleCloseDateModal}
        dateSlots={dateSlots}
        onDateSelected={onDateSelected}
        isBlocked={isBlocked}
      />

      <LoginRequiredModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onView={() => {
          trackCtaClick('signup_modal_view', {
            trigger_source: 'kpathport_workshop_card_cta',
            page_path: window.location.pathname,
          });
        }}
        onMethodSelect={(method) => {
          trackCtaClick('signup_method_select', {
            method,
            trigger_source: 'kpathport_workshop_card_cta',
          });
        }}
      />

      <QuickAddBlockedModal
        isOpen={showBlockedModal}
        onClose={() => setShowBlockedModal(false)}
      />
    </article>
  );
};

export default WorkshopCard;
